import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { TimeField } from '@mui/x-date-pickers';
import './EditScheduleModal.css';
import { ScheduledTask } from './Timeline';

interface EditScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedTasks: ScheduledTask[]) => void;
    tasks: ScheduledTask[];
}

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({ isOpen, onClose, onSave, tasks }) => {
    const [updatedTasks, setUpdatedTasks] = useState(tasks);

    // Refresh local state when tasks prop changes
    useEffect(() => {
        setUpdatedTasks(tasks);
    }, [tasks]);

    const handleTimeChange = (id: string, field: 'startTime' | 'endTime', value: Date | null) => {
        if (value) {
            const newTime = value.getHours() + value.getMinutes() / 60;
            setUpdatedTasks(prevTasks =>
                prevTasks.map(task => {
                    if (task.id === id) {
                        if (field === 'startTime') {
                            const duration = task.endTime - task.startTime;
                            return { ...task, startTime: newTime, endTime: newTime + duration };
                        } else {
                            return { ...task, endTime: newTime };
                        }
                    }
                    return task;
                })
            );
        }
    };

    const handleDelete = (id: string) => {
        setUpdatedTasks(prevTasks => prevTasks.filter(task => task.id !== id));
    };

    const calculateLength = (startTime: number, endTime: number) => {
        const lengthInHours = endTime - startTime;
        const hours = Math.floor(lengthInHours);
        const minutes = Math.round((lengthInHours % 1) * 60);
        return `${hours}h ${minutes}m`;
    };

    const handleSave = () => {
        onSave(updatedTasks);
        onClose();
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box className="custom-modal-box">
                <h2>Edit Schedule</h2>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className="table-cell-title">Title</TableCell>
                                <TableCell className="table-cell-description">Description</TableCell>
                                <TableCell className="table-cell-length">Estimated Length</TableCell>
                                <TableCell className="table-cell-time">Start Time</TableCell>
                                <TableCell className="table-cell-time">End Time</TableCell>
                                <TableCell className="table-cell-actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {updatedTasks.map((task) => (
                                <TableRow key={task.id}>
                                    <TableCell className="table-cell-title">{task.title}</TableCell>
                                    <TableCell className="table-cell-description">{task.description}</TableCell>
                                    <TableCell className="table-cell-length">{calculateLength(task.startTime, task.endTime)}</TableCell>
                                    <TableCell className="table-cell-time">
                                        <TimeField
                                            value={new Date(0, 0, 0, Math.floor(task.startTime), (task.startTime % 1) * 60)}
                                            onChange={(e) => handleTimeChange(task.id, 'startTime', e as Date)}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell className="table-cell-time">
                                        <TimeField
                                            value={new Date(0, 0, 0, Math.floor(task.endTime), (task.endTime % 1) * 60)}
                                            onChange={(e) => handleTimeChange(task.id, 'endTime', e as Date)}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell className="table-cell-actions">
                                        <Button onClick={() => handleDelete(task.id)} color="error">
                                            X
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div className="modal-actions">
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};

export default EditScheduleModal;
